import React, { useState, useEffect } from 'react';
import { SELFIE } from '..//enums/flow-type';
import { Plugins } from '@capacitor/core';

export const CameraContext = React.createContext({});

export const Camera = ({ children }) => {
  const [isInBodyScanMode, setIsInBodyScanMode] = useState(false);
  const [isCapturing, setIsCapturing] = useState(null);
  const [videoRef, setVideoRef] = useState(null);
  const [stream, setStream] = useState(null);
  const [cameraUnavailable, setCameraUnavailable] = useState(null);

  const photoFromStream = () => {
    if (!!videoRef && !!stream && isCapturing) {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const { width, height } = stream.getVideoTracks()[0].getSettings();
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(videoRef.current, 0, 0, width, height);

        const data = canvas.toDataURL('image/png');
        return data;
      } catch (err) {
        console.error(err);
        return null;
      }
    }
    return null;
  };

  const tryGetCamera = async (clientWidth, clientHeight, withoutFacing) => {
    const constraints = {
      video: {
        height: {
          ideal: clientHeight,
          max: clientHeight,
        },
      },
      audio: false,
    };
    if (isCapturing !== SELFIE) {
      delete constraints.video.height;
      constraints.video.width = {
        ideal: clientWidth,
        max: clientWidth,
      };
    }
    if (!withoutFacing) {
      constraints.video.facingMode = { exact: isCapturing };
    }
    console.log(isCapturing, constraints);
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    videoRef.current.height = clientHeight;
    videoRef.current.srcObject = stream;
    setStream(stream);
  };

  const initCameraAsync = async (clientWidth, clientHeight) => {
    try {
      await tryGetCamera(clientWidth, clientHeight, false);
    } catch (err) {
      console.error('initCamera error', err);
      try {
        await tryGetCamera(clientWidth, clientHeight, true);
      } catch (err2) {
        console.error('initCamera error 2', err2);
        setCameraUnavailable(true);
      }
    }
  };

  const initCamera = async () => {
    const { clientHeight, clientWidth } = videoRef.current.parentNode;
    console.log('.video-display dims w:h', clientWidth, clientHeight);
    try {
      const cameraPermState = await Plugins.Camera.checkPermissions();
      console.log('camera permission state', cameraPermState);
      switch (cameraPermState.state) {
        case 'denied':
          setCameraUnavailable(true);
          break;
        case 'granted':
          await initCameraAsync(clientWidth, clientHeight);
          break;
        default: {
          const permStatePrompt = await Plugins.Camera.requestPermissions({permissions: ['camera']});
          if (permStatePrompt.state === 'granted') {
            await initCameraAsync(clientWidth, clientHeight);
          } else {
            setCameraUnavailable(true)
          }
          break;
        }
      }
    } catch (permsErr) {
      console.error(permsErr);
      // seems to throw immediately if its just running in a browser rather than in an app
      await initCameraAsync(clientWidth, clientHeight);
    }
    console.error('cameraUnavailable', cameraUnavailable);
  };

  const disposeCameraIfRunning = () => {
    if (!!stream) {
      console.log('has camera stream');
      stream.getTracks().forEach((track, i) => {
        console.log('dispose camera stream', i);
        track.stop();
      });
      setStream(null);
    }
  };

  useEffect(() => {
    if (!isCapturing) {
      disposeCameraIfRunning();
    }
  }, [isCapturing]);

  useEffect(() => {
    console.log('videoRef in effect', videoRef, isCapturing);
    if (!!videoRef && !!isCapturing) {
      console.log('initCamera invoked in effect');
      initCamera();
    }
  }, [videoRef]);

  return (
    <CameraContext.Provider
      value={{
        isCapturing,
        setIsCapturing,
        videoRef,
        setVideoRef,
        photoFromStream,
        isInBodyScanMode,
        setIsInBodyScanMode,
        stream,
      }}
    >
      {children}
    </CameraContext.Provider>
  );
};
